/**
 * Atlas PM · 项目星图 · 布局算法（纯函数）
 *
 * ⚠️ 本文件**不依赖浏览器 / React / WebGL 上下文**，可在 Node 里直接 import 测试。
 *    只在最后用 THREE.Vector3 承载坐标。
 *
 * 约定（对照原型 prototype/js/star.js）：
 *   - 中心：项目核心节点，位于 (0,0,0)
 *   - 聚类：按【任务形态】分组，每组在球面上占一个扇区
 *   - 组内：斐波那契球面分布，节点不互相重叠
 *   - 规模自适应：任务很少时放大间距，很多时收缩半径与节点尺寸
 *
 * 数据来源说明：
 *   真实接口 `/tasks/tree/:projectId` 的 TaskTreeNode **没有 `form` 字段**，
 *   任务分类落在 `categoryId` / `categoryName` / `categoryColor`。
 *   因此聚类键的优先级为：`form`（若后端将来补充）→ `categoryId` → `categoryName` → 未分类。
 */

import * as THREE from 'three';
import { TASK_FORM_COLORS, TASK_FORM_LABELS, taskStatusLabel } from '@atlaspm/shared';
import type { TaskTreeNode } from '@atlaspm/shared';

/* ═══════════════════════════════════════════════════════════════
   状态着色（9 态）
   ═══════════════════════════════════════════════════════════════ */

/**
 * 9 态颜色映射。
 * 原型只给了 6 态，这里补齐 SUBMITTED / CANCELLED / ARCHIVED：
 *   - SUBMITTED        → #4FA8FF（蓝，进行中→审批中的过渡）
 *   - CANCELLED        → #5A6B7D（灰，失效）
 *   - ARCHIVED         → #3F4E60（深灰，归档）
 * ⚠️ shared 目前未导出状态色，建议补进 `@atlaspm/shared`（见回执）。
 */
export const STAR_STATE_COLORS: Readonly<Record<string, string>> = {
  DRAFT: '#8FB2D0',
  IN_PROGRESS: '#5EE7FF',
  SUBMITTED: '#4FA8FF',
  APPROVING: '#F0C060',
  REJECTED: '#FF6B8A',
  FROZEN: '#A77BFF',
  UNLOCKED_EDITING: '#A77BFF',
  CANCELLED: '#5A6B7D',
  ARCHIVED: '#3F4E60',
};

export const STAR_FALLBACK_STATE_COLOR = '#8FB2D0';

/** 聚类无显式颜色时的确定性回退色板（取自 shared 的任务形态配色语义） */
const FORM_PALETTE: readonly string[] = [
  '#5EE7FF',
  '#A77BFF',
  '#F0C060',
  '#54E0A8',
  '#4FA8FF',
  '#FF6B8A',
  '#7BD9C0',
  '#8FB2D0',
];

/** `#RRGGBB` / `RRGGBB` → THREE 用的 0xRRGGBB；非法值回退灰蓝 */
export function hexToNumber(hex: string): number {
  const h = hex.replace(/^#/, '');
  const v = Number.parseInt(h, 16);
  return Number.isFinite(v) ? v : 0x8fb2d0;
}

/** 状态 → 颜色（缺省灰蓝） */
export function colorForState(state: string): string {
  return STAR_STATE_COLORS[state] ?? STAR_FALLBACK_STATE_COLOR;
}

/** 状态 → 中文标签（P6.46 起统一走 @atlaspm/shared 单一数据源） */
export function labelForState(state: string): string {
  return taskStatusLabel(state);
}

/**
 * 聚类颜色：
 *   1) 显式 categoryColor（若合法）优先
 *   2) TASK_FORM_COLORS（若 form 命中内置 8 形态）
 *   3) 确定性色板（按 key 哈希，保证同 key 同色、跨渲染稳定）
 */
export function colorForForm(form: string, explicit?: string | null): string {
  if (explicit && /^#?[0-9a-fA-F]{6}$/.test(explicit)) {
    return explicit.startsWith('#') ? explicit : `#${explicit}`;
  }
  const fromForm = TASK_FORM_COLORS[form];
  if (fromForm) return fromForm;
  let h = 0;
  for (let i = 0; i < form.length; i++) {
    h = (h * 31 + form.charCodeAt(i)) >>> 0;
  }
  return FORM_PALETTE[h % FORM_PALETTE.length] ?? '#5EE7FF';
}

/* ═══════════════════════════════════════════════════════════════
   输入 / 输出类型
   ═══════════════════════════════════════════════════════════════ */

/** 拍平后的任务（星图节点输入） */
export interface StarTaskInput {
  id: string;
  name: string;
  code?: string;
  state: string;
  ownerName?: string | null;
  /** 任务形态（若后端提供；当前接口为 null） */
  form?: string | null;
  categoryId?: string | null;
  categoryName?: string | null;
  categoryColor?: string | null;
  progressPct?: number;
  /** 指标数量（决定节点大小） */
  metricTotal?: number;
  /** 逾期天数（决定脉冲与大小） */
  overdueDays?: number;
  parentId?: string | null;
}

/** 布局后的节点 */
export interface StarLayoutNode {
  id: string;
  name: string;
  code?: string;
  /** 聚类键（形态 / 分类 / UNCATEGORIZED） */
  form: string;
  /** 聚类显示名 */
  formLabel: string;
  state: string;
  pos: THREE.Vector3;
  home: THREE.Vector3;
  /** 0xRRGGBB */
  color: number;
  colorHex: string;
  /** 所属星座（聚类）的代表色，供依赖星轨按簇分色用 */
  clusterColorHex: string;
  size: number;
  isCenter: boolean;
  task?: StarTaskInput;
}

/** 一个星座（聚类） */
export interface StarCluster {
  key: string;
  label: string;
  colorHex: string;
  center: THREE.Vector3;
  count: number;
}

export interface StarLayout {
  nodes: StarLayoutNode[];
  center: StarLayoutNode;
  clusters: StarCluster[];
}

/* ═══════════════════════════════════════════════════════════════
   树 → 扁平
   ═══════════════════════════════════════════════════════════════ */

/** 逾期天数（无 planEnd 或已冻结/归档/取消 视为不逾期） */
function deriveOverdueDays(node: TaskTreeNode): number {
  const state = String(node.state ?? '');
  if (state === 'FROZEN' || state === 'ARCHIVED' || state === 'CANCELLED') return 0;
  const end = node.planEnd ? Date.parse(node.planEnd) : Number.NaN;
  if (!Number.isFinite(end)) return 0;
  /* P7.8：按自然日计算（忽略时分秒），与后端 todayUtcDate 同口径。 */
  const n = new Date();
  const today = Date.UTC(n.getFullYear(), n.getMonth(), n.getDate());
  const diff = today - end;
  return diff > 0 ? Math.floor(diff / 86_400_000) : 0;
}

/**
 * 把 `/tasks/tree/:projectId` 的树形结构**拍平**为扁平任务列表。
 * 保留 `parentId` 用于画父子连线。
 *
 * ⚠️ 拍平后节点数必须等于原树节点总数（自测 ⑨）。
 */
export function flattenTaskTree(roots: readonly TaskTreeNode[]): StarTaskInput[] {
  const out: StarTaskInput[] = [];
  const walk = (node: TaskTreeNode, parentId: string | null): void => {
    out.push({
      id: String(node.id),
      name: node.name,
      code: node.code,
      state: String(node.state),
      ownerName: node.ownerName ?? null,
      categoryId: node.categoryId ?? null,
      categoryName: node.categoryName ?? null,
      categoryColor: node.categoryColor ?? null,
      progressPct: typeof node.progressPct === 'number' ? node.progressPct : 0,
      metricTotal: typeof node.metricTotal === 'number' ? node.metricTotal : 0,
      overdueDays: deriveOverdueDays(node),
      parentId,
    });
    const children = Array.isArray(node.children) ? node.children : [];
    for (const child of children) walk(child, String(node.id));
  };
  for (const root of roots) walk(root, null);
  return out;
}

/* ═══════════════════════════════════════════════════════════════
   聚类解析
   ═══════════════════════════════════════════════════════════════ */

interface ClusterMeta {
  key: string;
  label: string;
  colorHex: string;
}

function resolveCluster(task: StarTaskInput): ClusterMeta {
  const form = (task.form ?? '').trim();
  if (form) {
    return {
      key: form,
      label: TASK_FORM_LABELS[form] ?? form,
      colorHex: colorForForm(form, task.categoryColor),
    };
  }
  if (task.categoryId || task.categoryName) {
    const key = task.categoryId ? `CAT:${task.categoryId}` : `CAT:${task.categoryName}`;
    return {
      key,
      label: task.categoryName || '未命名分类',
      colorHex: colorForForm(key, task.categoryColor),
    };
  }
  return { key: 'UNCATEGORIZED', label: '未分类', colorHex: '#8FB2D0' };
}

/* ═══════════════════════════════════════════════════════════════
   布局
   ═══════════════════════════════════════════════════════════════ */

/**
 * 计算星图布局。
 *
 * @param projectName 中心节点（项目核心）的名字
 * @param tasks       扁平任务列表
 */
export function layoutStar(projectName: string, tasks: readonly StarTaskInput[]): StarLayout {
  const center: StarLayoutNode = {
    id: '__center__',
    name: projectName || '项目',
    form: '__center__',
    formLabel: '项目核心',
    state: 'PROJECT',
    pos: new THREE.Vector3(0, 0, 0),
    home: new THREE.Vector3(0, 0, 0),
    color: hexToNumber('#5EE7FF'),
    colorHex: '#5EE7FF',
    clusterColorHex: '#5EE7FF',
    size: 2.3,
    isCenter: true,
  };

  // 按聚类键分组
  const groups = new Map<string, StarTaskInput[]>();
  const metaByKey = new Map<string, ClusterMeta>();
  for (const task of tasks) {
    const meta = resolveCluster(task);
    if (!metaByKey.has(meta.key)) metaByKey.set(meta.key, meta);
    const arr = groups.get(meta.key);
    if (arr) arr.push(task);
    else groups.set(meta.key, [task]);
  }

  const forms = [...groups.keys()];
  const total = tasks.length;

  // 规模自适应：
  //   density   —— 任务多时收缩半径 / 节点，避免糊成一团
  //   spreadUp  —— 任务少时拉伸间距，避免挤在一点
  const density = total > 200 ? 0.5 : total > 120 ? 0.62 : total > 80 ? 0.72 : total > 40 ? 0.85 : 1;
  const spreadUp = total < 5 ? 1.35 : total < 10 ? 1.12 : 1;

  // 星座环半径：形态多则扩大，少则收紧
  const R0 = forms.length <= 4 ? 15 : forms.length <= 6 ? 19 : 24;

  const nodes: StarLayoutNode[] = [center];
  const clusters: StarCluster[] = [];

  forms.forEach((key, fi) => {
    const arr = groups.get(key) ?? [];
    const meta = metaByKey.get(key) ?? { key, label: key, colorHex: '#5EE7FF' };

    const a0 = (fi / forms.length) * Math.PI * 2 - Math.PI / 2;
    const ringR = (R0 + (fi % 2) * 6.5) * spreadUp;
    const cx = Math.cos(a0) * ringR;
    const cz = Math.sin(a0) * ringR;
    const cy = ((fi % 3) - 1) * 4.2;

    clusters.push({
      key,
      label: meta.label,
      colorHex: meta.colorHex,
      center: new THREE.Vector3(cx, cy, cz),
      count: arr.length,
    });

    const n = arr.length;
    arr.forEach((task, ti) => {
      // 斐波那契球面分布：均匀、无重叠（节点数=1 时也落在球面上，不缩成一个点）
      const cosPhi = Math.max(-1, Math.min(1, 1 - (2 * (ti + 0.5)) / n));
      const phi = Math.acos(cosPhi);
      const theta = Math.PI * (1 + Math.sqrt(5)) * (ti + 0.5);

      const overdue = task.overdueDays ?? 0;
      const metric = Math.min(Math.max(task.metricTotal ?? 0, 0), 12);
      // 组内球半径：随组内任务数增长；少任务时保底散布半径
      const minR = n <= 2 ? 2.6 : 2.0;
      let rr = Math.max(minR, 1.9 + Math.sqrt(n) * 0.78 + (overdue > 0 ? 0.55 : 0));
      rr *= density * spreadUp;

      const px = cx + Math.sin(phi) * Math.cos(theta) * rr;
      const py = cy + Math.cos(phi) * rr * 0.8;
      const pz = cz + Math.sin(phi) * Math.sin(theta) * rr;

      const colorHex = colorForState(task.state);
      const sizeBase = 0.72 + metric * 0.07 + (overdue > 0 ? 0.26 : 0);
      const size = sizeBase * (total < 5 ? 1.2 : 1) * (total > 120 ? 0.7 : 1);

      nodes.push({
        id: task.id,
        name: task.name,
        code: task.code,
        form: key,
        formLabel: meta.label,
        state: task.state,
        pos: new THREE.Vector3(px, py, pz),
        home: new THREE.Vector3(px, py, pz),
        color: hexToNumber(colorHex),
        colorHex,
        clusterColorHex: meta.colorHex,
        size,
        isCenter: false,
        task,
      });
    });
  });

  return { nodes, center, clusters };
}
